import { Box, useTheme, useMediaQuery } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import IFundTypeHistoryPeriod from '../../../../interfaces/expertise/history/IFundTypeHistoryPeriod';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';

import am5locales_en_US from '@amcharts/amcharts5/locales/en_US';
import am5locales_fr_FR from '@amcharts/amcharts5/locales/fr_FR';
import am5themes_Responsive from '@amcharts/amcharts5/themes/Responsive';
import { useTranslation } from 'react-i18next';
import { useAppSettings } from '../../../../../../app/features/settings/providers/AppSettingsProvider';
import investmentTypeHistoryLang from '../../langs/investmentTypeHistoryLang';
import AppLoaderGraph from '../../../../../../app/features/loader/graph/AppLoaderGraph';
import _ from 'lodash';
import langInvestWordLang from '../../../../langs/langInvestWordLang';
import useAppFormatAmount from '../../../../../../app/features/format/hooks/useAppFormatAmount';
import CAppPaper from '../../../../../../app/components/CAppPaper';
import investmentFundLang from '../../../../langs/investmentFundLang';
import useAppFormatNumber from '../../../../../../app/features/format/hooks/useAppFormatNumber';

type CHistoryMultipleNetOfFeesProps = {
	historyPeriods: IFundTypeHistoryPeriod[] | null | undefined;
	btnPrevious: ReactNode;
	btnNext: ReactNode;
	allHistoryPeriods: IFundTypeHistoryPeriod[] | null | undefined;
};
const CHistoryMultipleNetOfFees = (props: CHistoryMultipleNetOfFeesProps) => {
	const { historyPeriods, btnPrevious, btnNext, allHistoryPeriods } = props;

	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { rounding } = useAppSettings();
	const { displayRounding, displayRoundingSuffix } = useAppFormatAmount();
	const { formatAmount } = useAppFormatNumber();

	const darkModeEnabled = theme.palette.mode === 'dark';

	const [data, setData] = useState<Record<string, unknown>[] | null>(null);

	useEffect(() => {
		if (historyPeriods && allHistoryPeriods) {
			// Init des data
			setData(
				historyPeriods.map((x, index) => {
					// Total Value
					const hideTotalValueDisplay =
						(historyPeriods[index + 1] && historyPeriods[index + 1].stat.tvpi === null) ||
						(historyPeriods[index] && historyPeriods[index].stat.tvpi === null)
							? true
							: false;
					const hideTotalValuePoint =
						historyPeriods[index] && historyPeriods[index].stat.tvpi === null ? true : false;

					return {
						quarter: x.name,
						// Total Value
						tvpi: x.stat.tvpi,
						tvpiText: x.stat.tvpi ? formatAmount(x.stat.tvpi) : t(langInvestWordLang.nc.tag),
						tvpiStrokeSettings: {
							strokeOpacity: hideTotalValueDisplay ? 0 : 1,
						},
						tvpiFillSettings: {
							fillOpacity: hideTotalValueDisplay ? 0 : 1,
						},
						tvpiBulletSettings: {
							opacity: hideTotalValuePoint ? 0 : 1,
							fillOpacity: hideTotalValuePoint ? 0 : 1,
							strokeOpacity: hideTotalValuePoint ? 0 : 1,
							radius: hideTotalValuePoint ? 0 : 4,
							strokeWidth: hideTotalValuePoint ? 0 : 0,
						},
						tvpiBulletTextSettings: {
							populateText: hideTotalValuePoint ? false : true,
							text: hideTotalValuePoint ? '' : '{tvpi}',
						},
					};
				}),
			);
		}
	}, [allHistoryPeriods, displayRounding, displayRoundingSuffix, formatAmount, historyPeriods, rounding, t]);

	const [yMax, setYMax] = useState<number | null>(null);
	useEffect(() => {
		if (allHistoryPeriods) {
			setYMax(_.max(allHistoryPeriods.map((x) => _.max([x.stat.tvpi]))) ?? null);
		}
	}, [allHistoryPeriods, displayRounding, rounding]);

	useEffect(() => {
		if (data && yMax) {
			const root = am5.Root.new('tvpi-graph');

			const chart = root.container.children.push(
				am5xy.XYChart.new(root, {
					layout: root.verticalLayout,
				}),
			);

			const lineXAxisColor = am5.Theme.new(root);
			lineXAxisColor.rule('Grid', ['base']).setAll({
				strokeOpacity: 1,
				stroke: am5.color('#1a2b48'),
				strokeWidth: 2,
			});

			if (darkModeEnabled) {
				root.setThemes([am5themes_Dark.new(root), am5themes_Responsive.new(root), lineXAxisColor]);
			} else {
				root.setThemes([am5themes_Responsive.new(root), lineXAxisColor]);
			}

			if (i18n.language === 'fr') {
				root.locale = am5locales_fr_FR;
			} else {
				root.locale = am5locales_en_US;
			}

			const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
			cursor.lineY.set('visible', false);
			cursor.lineX.set('visible', false);

			const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
			xRenderer.labels.template.setAll({
				centerY: am5.p50,
				paddingTop: 10,
				paddingBottom: 20,
				paddingRight: 15,
				textAlign: 'center',
				fontSize: '13px',
				fontFamily: "'Poppins', sans-serif",
				fontWeight: '500',
				fill: am5.color('#1A2B48'),
			});

			xRenderer.grid.template.setAll({
				visible: false,
			});

			const xAxis = chart.xAxes.push(
				am5xy.CategoryAxis.new(root, {
					maxDeviation: 0.3,
					categoryField: 'quarter',
					opacity: 1,
					renderer: xRenderer,
				}),
			);

			const yAxis = chart.yAxes.push(
				am5xy.ValueAxis.new(root, {
					min: 0,
					max: yMax + (yMax * 20) / 100,
					maxDeviation: 0.3,
					renderer: am5xy.AxisRendererY.new(root, {}),
				}),
			);

			const yRenderer = yAxis.get('renderer');
			yRenderer.labels.template.setAll({
				fill: am5.color(darkModeEnabled ? '#ccc' : '#AAA'),
				fontSize: '10px',
			});

			// Multiple TVPI
			const series = chart.series.push(
				am5xy.LineSeries.new(root, {
					name: t(investmentTypeHistoryLang.graph_legend_multiple_net_of_fees.tag),
					xAxis: xAxis,
					yAxis: yAxis,
					valueXField: 'quarter',
					valueYField: 'tvpi',
					fill: am5.color(0x2b70e7),
					stroke: am5.color(0x2b70e7),
					seriesTooltipTarget: 'bullet',
					sequencedInterpolation: true,
					categoryXField: 'quarter',
				}),
			);

			series.strokes.template.setAll({
				strokeWidth: 2,
				templateField: 'tvpiStrokeSettings',
			});

			series.fills.template.setAll({
				templateField: 'tvpiFillSettings',
			});

			// Add Label on top of bullet
			series.bullets.push(function () {
				return am5.Bullet.new(root, {
					locationY: 0.5,
					sprite: am5.Label.new(root, {
						fontSize: '12px',
						fill: am5.color(theme.palette.primary.main),
						centerY: am5.percent(100),
						centerX: am5.percent(50),

						templateField: 'tvpiBulletTextSettings',
					}),
				});
			});

			// Add Circle for each bullet
			series.bullets.push(function () {
				const circle = am5.Circle.new(root, {
					stroke: am5.color('#2b70e7'),
					interactive: true,
					fill: am5.color(0xffffff),
					templateField: 'tvpiBulletSettings',
				});

				circle.states.create('hover', {
					opacity: 1,
					radius: 4,
				});

				return am5.Bullet.new(root, {
					sprite: circle,
				});
			});

			xAxis.data.setAll(data);
			series.data.setAll(data);

			series.appear(1000);

			chart.appear(1000, 100);

			cursor.setAll({
				xAxis: xAxis,
				yAxis: yAxis,
			});

			const cursorStroke = chart.get('cursor');
			cursorStroke?.lineY.setAll({
				visible: false,
			});
			cursorStroke?.lineX.setAll({
				visible: false,
			});

			let legend: am5.Legend;
			let legendRoot: am5.Root;

			if (isMobile) {
				legendRoot = am5.Root.new('tvpi-graph-legend');
				legend = legendRoot.container.children.push(
					am5.Legend.new(legendRoot, {
						centerX: am5.percent(50),
						x: am5.percent(50),
						layout: legendRoot.verticalLayout,
						useDefaultMarker: true,
						height: am5.percent(100),
						verticalScrollbar: am5.Scrollbar.new(legendRoot, {
							orientation: 'vertical',
						}),
					}),
				);

				legend.markerRectangles.template.setAll({
					cornerRadiusTL: 10,
					cornerRadiusTR: 10,
					cornerRadiusBL: 10,
					cornerRadiusBR: 10,
				});

				legend?.labels.template.setAll({
					fontSize: 14,
					fontWeight: '300',
					fill: am5.color(theme.palette.primary.main),
				});
				legend?.valueLabels.template.setAll({
					fontSize: 14,
					fontWeight: '400',
					fill: am5.color(theme.palette.primary.main),
				});
			} else {
				legend = chart.children.push(
					am5.Legend.new(root, {
						centerX: am5.p50,
						x: am5.p50,
						y: am5.percent(90),
					}),
				);
			}

			legend.data.setAll(chart.series.values);

			return () => {
				root.dispose();
				isMobile ? legendRoot.dispose() : null;
			};
		}
	}, [data, t, darkModeEnabled, rounding, yMax, isMobile, i18n.language, theme.palette.primary.main]);

	return (
		<>
			{data !== null ? (
				<>
					{data.length === 0 ? (
						<>
							<CAppPaper
								variant="outlined"
								sx={{
									textAlign: 'center',
									padding: '50px',
									width: '100%',
								}}
							>
								{t(investmentFundLang.text_no_data_available_yet.tag)}
							</CAppPaper>
						</>
					) : (
						<>
							<Box sx={{ position: 'relative', height: '300px' }}>
								<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', left: '0px' }}>
									{btnPrevious}
								</Box>
								<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', right: '0px' }}>
									{btnNext}
								</Box>
								<Box id="tvpi-graph" sx={{ height: '300px', width: '100%', marginLeft: '-10px' }} />
							</Box>
							<Box
								id="tvpi-graph-legend"
								sx={{ height: '100px', display: { xs: 'block', sm: 'none' }, marginBottom: '20px' }}
							/>
						</>
					)}
				</>
			) : (
				<AppLoaderGraph height={280} />
			)}
		</>
	);
};

export default CHistoryMultipleNetOfFees;
